import { axiosInstance } from '@/utils/axios';

const CLIENT_SESSION_STORAGE_KEY = 'h2o_client_session_id';

function canUseBrowser() {
  return typeof window !== 'undefined';
}

function isValidSessionId(value) {
  return typeof value === 'string' && /^[a-zA-Z0-9_-]{8,128}$/.test(value);
}

function createSessionId() {
  if (canUseBrowser() && window.crypto?.randomUUID) {
    return window.crypto.randomUUID().replace(/-/g, '');
  }

  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

function resolveApiUrl(path) {
  const normalizedPath = String(path || '').trim();
  if (!normalizedPath) return '';
  if (/^https?:\/\//i.test(normalizedPath)) return normalizedPath;

  const baseUrl = axiosInstance?.defaults?.baseURL || '';
  if (!baseUrl) return normalizedPath;

  const base = String(baseUrl).replace(/\/$/, '');
  const suffix = normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;
  return `${base}${suffix}`;
}

export function getClientSessionId() {
  if (!canUseBrowser()) return '';

  try {
    const existing = window.localStorage.getItem(CLIENT_SESSION_STORAGE_KEY);
    if (isValidSessionId(existing)) return existing;

    const nextValue = createSessionId();
    window.localStorage.setItem(CLIENT_SESSION_STORAGE_KEY, nextValue);
    return nextValue;
  } catch {
    return createSessionId();
  }
}

export function getCurrentPagePath() {
  if (!canUseBrowser()) return '';
  return `${window.location.pathname || '/'}${window.location.search || ''}${window.location.hash || ''}`;
}

export function buildTrackedRedirectUrl(resourceType, resourceId, target, sourcePath = '') {
  const normalizedResourceType = String(resourceType || '').trim();
  const normalizedResourceId = String(resourceId || '').trim();
  const normalizedTarget = String(target || '').trim();

  if (!normalizedResourceType || !normalizedResourceId || !normalizedTarget) return '';

  const params = new URLSearchParams();
  const clientSessionId = getClientSessionId();
  if (clientSessionId) {
    params.set('sid', clientSessionId);
  }

  const originPath = String(sourcePath || getCurrentPagePath()).trim();
  if (originPath) {
    params.set('from', originPath);
  }

  params.set('target', normalizedTarget);

  return resolveApiUrl(`/api/v1/analytics/redirect/${encodeURIComponent(normalizedResourceType)}/${encodeURIComponent(normalizedResourceId)}?${params.toString()}`);
}
